import { createContext, useState, useEffect, useContext, useRef, PropsWithChildren } from 'react';
import AuthData                                                              from 'lib/authData';
import { registerAuthErrorHandler }                                          from 'lib/api/cms';
import { subscribeToAuthChanges }                                            from 'lib/authChange';
import { notify }                                                            from './AppNotifications';

/**
 * localStorage key holding the **referral** (anonymous) journey's user.
 *
 * Kept separate from `AuthData`'s `oso-auth` because the two journeys have
 * genuinely different lifecycles, not because they are different auth models —
 * see the note on referral session validity at the bottom of this file.
 */
export const REFERRAL_USER_KEY = 'user';

const AuthContext = createContext<IAuthContext>({} as IAuthContext);

/** Read the referral user, treating anything expired or malformed as absent. */
const readReferralUser = (): AuthUser | null => {
  let raw: string | null;
  try {
    raw = localStorage.getItem(REFERRAL_USER_KEY);
  } catch (_e) {
    return null; // localStorage unavailable
  }
  if (!raw) return null;

  let userData: AuthUser;
  try {
    userData = JSON.parse(raw) as AuthUser;
  } catch (_e) {
    return null; // corrupted
  }

  const expires = new Date(userData?.tokenExpiry).getTime();
  if (isNaN(expires)) return null; // tokenExpiry missing or corrupted
  if (expires < Date.now()) return null;

  return userData;
};

/**
 * The signed-in staff/consumer user, from the profile cache that the boot
 * session probe populates. Read on every render, deliberately: a sign-in
 * completes through redux and the sagas, outside this context, and this is how
 * that becomes visible here without a second source of truth.
 */
const readStandardUser = (): StandardUser | null => {
  try {
    return (AuthData.get()?.user as StandardUser) ?? null;
  } catch (_e) {
    return null;
  }
};

/**
 * CD-8740 — a single auth model for both journeys.
 *
 * Before this change the SPA had two disconnected notions of "signed in": a raw
 * `localStorage.getItem('oso-auth')` truthiness check for staff, and this
 * context for the anonymous referral journey. Consumers had to know about both.
 * They are now unified behind `{ user, mode }`; `mode` is what tells the two
 * apart, and nothing outside this file reads either store directly.
 *
 * Neither store is a credential and neither is authoritative about the session.
 * For the standard journey the server is (`PUT /sessions` at boot); for the
 * referral journey the `referral` cookie is.
 */
export const AuthProvider = ({ children, onStandardSessionExpired }: AuthProviderProps) => {
  const [referralUser, setReferralUser] = useState<AuthUser | null>(readReferralUser);

  // The profile is read below on every render, which is enough while it only
  // changes through flows that navigate. The boot probe's late result (after
  // BOOT_PROBE_TIMEOUT_MS) changes it with nothing to trigger a render, so it
  // announces itself (lib/authChange) and this bump is the render.
  const [, setProfileVersion] = useState(0);
  useEffect(() => subscribeToAuthChanges(() => setProfileVersion((version) => version + 1)), []);

  const standardUser = readStandardUser();

  // A referral takes precedence: `signIn` drops the standard profile, so the two
  // only overlap for the instant between the two writes.
  const user: CurrentUser | null = referralUser ?? standardUser;
  const mode: AuthMode | null = referralUser ? 'referral' : standardUser ? 'standard' : null;

  // The 401 handler below is registered exactly once (registering again would
  // stack a second interceptor), so everything it touches must be read at call
  // time, not captured at mount: the prop through this ref, the two journeys
  // through their reads.
  const onStandardSessionExpiredRef = useRef(onStandardSessionExpired);
  useEffect(() => {
    onStandardSessionExpiredRef.current = onStandardSessionExpired;
  });

  // A page with several in-flight cms requests gets several 401s at once; the
  // session only ends once. The route change to /signout re-renders this
  // provider while the profile is still cached (it survives until the SIGNOUT
  // saga finishes DELETE /sessions), so re-arm only once the session has gone,
  // not on any render that still sees it, or a second in-flight 401 would toast
  // and navigate again.
  const standardExpiryHandledRef = useRef(false);
  useEffect(() => {
    if (!standardUser) standardExpiryHandledRef.current = false;
  });

  /**
   * A 401 from the cms client means the cookie session behind it is gone.
   *
   * - Referral journey: unchanged by CD-8740 — end the referral state here;
   *   the `referral` cookie itself is server-owned.
   * - Standard journey: this used to only clear referral state, leaving a
   *   staff/consumer user on a private page with a dead session and a toast
   *   claiming they were signed out. Route through the real signout instead
   *   (`onStandardSessionExpired` → /signout → the SIGNOUT saga), the same
   *   teardown the hourly refresh uses when it meets a 401.
   * - No session in either journey: a stray 401 after signout — nothing to end,
   *   so no toast.
   */
  const handleAuthError = () => {
    if (readReferralUser()) {
      notify.info('You have been automatically signed out. Please re-enter your details to continue.', { title: 'Session expired' });
      signOut();
      return;
    }

    if (readStandardUser() && !standardExpiryHandledRef.current) {
      standardExpiryHandledRef.current = true;
      notify.info('You have been automatically signed out. Please re-enter your details to continue.', { title: 'Session expired' });
      onStandardSessionExpiredRef.current?.();
    }
  };

  /** Start an anonymous referral journey, ending any standard session first (AC11). */
  const signIn = (userData: AuthUser) => {
    setReferralUser(userData);
    AuthData.drop();
  };

  /** End the anonymous referral journey. Staff sign-out goes through the SIGNOUT saga. */
  const signOut = () => {
    setReferralUser(null);
  };

  useEffect(() => {
    if (referralUser) {
      localStorage.setItem(REFERRAL_USER_KEY, JSON.stringify(referralUser));
    } else {
      localStorage.removeItem(REFERRAL_USER_KEY);
    }
  }, [JSON.stringify(referralUser)]);

  // Register the auth error handler to handle 401 errors. The cms client renews a
  // standard session and replays before it gets here (see `registerAuthErrorHandler`),
  // so `handleAuthError` only sees a 401 once the session is truly over; the
  // referral journey has no refresh endpoint, so it is never renewed.
  useEffect(() => {
    registerAuthErrorHandler(handleAuthError, { canRenew: () => !readReferralUser() });
  }, []);

  const contextValue = {
    user,
    mode,
    referralUser,
    signIn,
    signOut,
  };

  return (
    <AuthContext.Provider value={contextValue}>{children}</AuthContext.Provider>
  );
};

// Custom hook to consume the AuthContext
export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const withAuthContext = (Component: React.ComponentType<any>) => {
  const WrappedComponent = (props: any) => {
    const authProps = useAuth();
    return <Component {...props} authContext={authProps} />;
  }
  WrappedComponent.displayName = `withAuth(${Component.displayName || Component.name || 'Component'})`;
  return WrappedComponent;
}

export type AuthMode = 'standard' | 'referral';

export type AuthProviderProps = PropsWithChildren<{
  /**
   * Called when a cms-client 401 arrives while a **standard** session is
   * active. The app wires this to `history.push('/signout')` so the SIGNOUT
   * saga performs the full teardown; tests may omit it.
   */
  onStandardSessionExpired?: () => void;
}>;

export interface IAuthContext {
  /** The current user in either journey, or null. Never a credential. */
  user: CurrentUser | null;
  /** Which journey `user` belongs to. */
  mode: AuthMode | null;
  /** The referral user specifically, for code that must not treat staff as anonymous. */
  referralUser: AuthUser | null;
  signIn: (userData: AuthUser) => void;
  signOut: () => void;
}

/**
 * The referral journey's user.
 *
 * **Known limitation, unchanged by CD-8740 and recorded here deliberately:**
 * referral session validity is judged purely client-side from `tokenExpiry`,
 * and there is no referral refresh mechanism at all. When the `referral` cookie
 * expires server-side the SPA does not find out until a request 401s. Bringing
 * the referral journey onto the same server-authoritative model as the standard
 * one is follow-up work, deliberately out of scope for CD-8740.
 */
export interface AuthUser {
  userId: number;
  role: string;
  tokenExpiry: number;
  refreshExpiry: number;
}

/** The staff/consumer profile returned by oso-api's auth responses. */
export interface StandardUser {
  id?: number;
  role?: string;
  [key: string]: unknown;
}

export type CurrentUser = AuthUser | StandardUser;
